import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bug, ArrowLeft, BookOpen, MessageCircle, Map } from "lucide-react";
import { EditorSection } from "../components/editor/EditorSection";
import { ResultViewer } from "../components/results/ResultViewer";
import { Toast } from "../components/common/Toast";
import { ErrorBanner } from "../components/common/ErrorBanner";
import { Loading } from "../components/common/Loading";
import { TraceLogsModal } from "../components/TraceLogsModal";
import { useResize } from "../hooks/useResize";
import { QUERY_LOADING_STAGE_LABEL, useQueryLoadingStage } from "../hooks/useQueryLoadingStage";
import { useQueryExecution } from "../hooks/useQueryExecution";
import { useSqlQuery } from "../hooks/useSqlQuery";
import type { TelegramUser } from "../types";
import { useMobile } from "../hooks/useMobile";
import { OnboardingTour } from "../components/OnboardingTour";
import { AITutorEditorPanel } from "../components/AITutorEditorPanel";
import type { AITutorDualMode } from "../components/AITutorDualModePanel";
import { useOnboardingCompleted } from "../hooks/useOnboardingCompleted";
import {
  AI_TUTOR_ASIDE_WIDTH_KEY,
  AI_TUTOR_MIN_WIDTH,
  AI_TUTOR_MAX_WIDTH,
  AI_TUTOR_DEFAULT_WIDTH,
} from "../constants/aiTutorLayout";
import CryptoJS from "crypto-js";
import { SECRET_KEY } from "../constants";

const DEFAULT_SANDBOX_SQL = `WITH 
  '🎲 ' as dice_roll,
  rand() % 6 + 1 AS roll_value
SELECT 
  concat(dice_roll, ' ', toString(roll_value)) as value,
  count() / sum(count()) over() as lucky_chance,
  bar(lucky_chance, 0, 1) as distribution
FROM numbers(100)
GROUP BY roll_value
ORDER BY roll_value`;

export const Sandbox = ({ user }: { user: TelegramUser | null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();
  const [sql, setSql] = useState(DEFAULT_SANDBOX_SQL);
  const { formatSql, shareQuery, toast } = useSqlQuery(sql, setSql, null);
  const [shouldAutoRun, setShouldAutoRun] = useState(false);
  
  // Загружаем SQL из URL параметра query при монтировании или используем дефолтный прекод
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("query");
    
    if (!query) {
      // Открытие Sandbox без параметров — показываем и сразу запускаем дефолтный прекод
      setSql(DEFAULT_SANDBOX_SQL);
      setShouldAutoRun(true);
      return;
    }

    try {
      const bytes = CryptoJS.AES.decrypt(decodeURIComponent(query), SECRET_KEY);
      const originalSql = bytes.toString(CryptoJS.enc.Utf8);
      if (originalSql) {
        setSql(originalSql);
        setShouldAutoRun(true);
        return;
      }
    } catch {
      console.warn("Не удалось расшифровать запрос из URL, используем прекод по умолчанию");
    }

    // Фолбэк, если query есть, но расшифровка не удалась
    setSql(DEFAULT_SANDBOX_SQL);
    setShouldAutoRun(true);
  }, [location.search]);
  const [isTraceModalOpen, setIsTraceModalOpen] = useState(false);
  const [errorBannerVisible, setErrorBannerVisible] = useState(true);
  const onboardingStartRef = useRef<(() => void) | null>(null);
  const {
    completedPages: onboardingCompletedPages,
    isLoading: onboardingStatusLoading,
    refetch: refetchOnboardingStatus,
  } = useOnboardingCompleted(user);
  const [showAITutor, setShowAITutor] = useState(false);
  const [tutorAsideMode, setTutorAsideMode] = useState<AITutorDualMode>("chat");
  
  const handleBugReport = () => {
    window.open("https://t.me/quantilecampbot", "_blank");
  };

  const handleDocumentation = () => {
    navigate("/docs");
  };

  const handleBack = () => {
    navigate("/simulator");
  };

  const {
    status,
    errorMsg,
    errorMsgFull,
    result,
    resultColumns,
    resultGroups,
    executionTimeMs,
    memoryUsageBytes,
    readBytes,
    readRows,
    writtenBytes,
    writtenRows,
    resultBytes,
    resultRows,
    cpuTimeUs,
    traceLogs,
    isLoading,
    runQuery,
    runQueryDebug,
    resetResult,
  } = useQueryExecution({
    taskId: 0,
    sql,
    onProgressSave: undefined,
  });

  const queryLoadingPhase = useQueryLoadingStage(isLoading);

  // Если пришёл прекод из URL, один раз автоматически запускаем его
  useEffect(() => {
    if (!shouldAutoRun) return;
    const trimmed = sql.trim();
    if (!trimmed) {
      setShouldAutoRun(false);
      return;
    }
    runQuery(false);
    setShouldAutoRun(false);
  }, [shouldAutoRun, sql, runQuery]);

  // Обработчик для Debug кнопки
  const handleDebug = async () => {
    setIsTraceModalOpen(true);
    await runQueryDebug();
  };

  const editorResize = useResize({
    initialSize: isMobile ? 400 : 300,
    minSize: isMobile ? 200 : 100,
    maxSize: isMobile ? 600 : 800,
    direction: "vertical",
  });

  const [chatPanelMinWidth, setChatPanelMinWidth] = useState(436);
  const aiTutorInitialWidth = useState(() => {
    try {
      const w = localStorage.getItem(AI_TUTOR_ASIDE_WIDTH_KEY);
      if (w) {
        const n = parseInt(w, 10);
        if (n >= AI_TUTOR_MIN_WIDTH && n <= AI_TUTOR_MAX_WIDTH) return n;
      }
    } catch {}
    return AI_TUTOR_DEFAULT_WIDTH;
  })[0];

  const aiTutorResize = useResize({
    initialSize: aiTutorInitialWidth,
    minSize: Math.max(AI_TUTOR_MIN_WIDTH, chatPanelMinWidth),
    maxSize: AI_TUTOR_MAX_WIDTH,
    direction: "horizontal",
    invertHorizontal: true,
    onSizeChange: (s) => {
      try {
        localStorage.setItem(AI_TUTOR_ASIDE_WIDTH_KEY, String(s));
      } catch {}
    },
  });

  // Показываем ErrorBanner при появлении новой ошибки
  useEffect(() => {
    if (errorMsg) {
      setErrorBannerVisible(true);
    }
  }, [errorMsg]);

  // AI Tutor: по умолчанию открыт на десктопе, закрыт на мобильном
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handleChange = () => {
      setShowAITutor(mq.matches);
    };
    handleChange();
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  const queryMetrics =
    executionTimeMs != null || readRows != null || readBytes != null
      ? {
          execution_time_ms: executionTimeMs ?? undefined,
          read_rows: readRows ?? undefined,
          read_bytes: readBytes ?? undefined,
          memory_usage_bytes: memoryUsageBytes ?? undefined,
          cpu_time_us: cpuTimeUs ?? undefined,
        }
      : undefined;

  const onboardingSteps = useMemo(() => [
    {
      id: "editor",
      target: "[data-onboarding-id='sql-editor']",
      title: "SQL редактор",
      content: "Напишите здесь ваш SQL-запрос. Редактор поддерживает автодополнение таблиц, колонок и функций ClickHouse. Используйте кнопку 'Форматировать' для красивого форматирования кода.",
      position: "bottom" as const,
    },
    {
      id: "run-button",
      target: "[data-onboarding-id='run-button']",
      title: "Запуск запроса",
      content: "Кнопка 'Запуск' выполнит ваш SQL-запрос и покажет результаты. Используйте её для проверки работы запроса.",
      position: "top" as const,
    },
    {
      id: "results",
      target: "[data-onboarding-id='results']",
      title: "Результаты запроса",
      content: "Здесь отображаются результаты выполнения вашего SQL-запроса. Вы можете копировать данные, строить графики и просматривать статистику выполнения запроса.",
      position: "top" as const,
    },
    {
      id: "table-chart-toggle",
      target: "[data-onboarding-id='table-chart-toggle']",
      title: "Переключение таблица/график",
      content: "Используйте эти кнопки для переключения между табличным представлением результатов и графиком. График поможет визуализировать данные и найти закономерности.",
      position: "left" as const,
    },
  ], []);

  return (
    <>
      <OnboardingTour 
        steps={onboardingSteps} 
        storageKey="onboarding-sandbox-completed"
        user={user}
        onStartRef={onboardingStartRef}
        onComplete={refetchOnboardingStatus}
      />
      <div
        className={`fixed inset-0 w-full h-full flex overflow-hidden bg-neutral-light ${isMobile ? "flex-col" : ""}`}
        style={{
          top: "var(--header-height, 4.5rem)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >

        {/* Кнопка развернуть AI Tutor (desktop), когда панель закрыта */}
        {!isMobile && !showAITutor && (
          <div className="hidden lg:flex fixed right-0 top-[calc(var(--header-height,4.5rem)+0.25rem)] z-30 flex-col gap-1">
            <button
              type="button"
              onClick={() => {
                setTutorAsideMode("chat");
                setShowAITutor(true);
              }}
              className="flex h-12 min-w-[4.5rem] px-3 items-center justify-center gap-2 bg-white border-2 border-neutral-border border-r-0 rounded-l-lg shadow-md hover:bg-primary/5 hover:border-primary/40 transition-colors font-medium text-neutral-text hover:text-primary"
              aria-label="Открыть чат AI Tutor"
              title="Чат"
            >
              <MessageCircle className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">Чат</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setTutorAsideMode("roadmap");
                setShowAITutor(true);
              }}
              className="flex h-12 min-w-[4.5rem] px-3 items-center justify-center gap-2 bg-white border-2 border-neutral-border border-r-0 rounded-l-lg shadow-md hover:bg-primary/5 hover:border-primary/40 transition-colors font-medium text-neutral-text hover:text-primary"
              aria-label="Открыть Roadmap"
              title="Roadmap"
            >
              <Map className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">Roadmap</span>
            </button>
          </div>
        )}

        {/* Основная часть: редактор, результаты и AI Tutor */}
        <div
          className={`flex-1 flex ${isMobile ? "flex-col" : "flex-row"} ${
            isMobile ? "overflow-auto" : "overflow-hidden"
          } relative`}
          style={!isMobile ? { height: "calc(100vh - var(--header-height, 4.5rem))" } : {}}
        >
          {/* Центральная область: редактор и результаты */}
          <div
            className={`${
              isMobile ? "w-full" : "flex-1 min-w-0"
            } flex flex-col ${isMobile ? "overflow-auto" : "overflow-hidden"} relative`}
          >
            <div
              className={`${
                isMobile ? "w-full" : "flex-1"
              } flex flex-col ${isMobile ? "" : "min-h-0"} relative w-full max-w-6xl mx-auto px-4 sm:px-5 md:px-6`}
            >
              {/* Верхняя панель действий — единая для desktop и mobile */}
              <div className="pt-2 pb-2 flex gap-1.5 sm:gap-2 flex-shrink-0 items-center overflow-x-auto min-w-0">
                <button
                  onClick={handleBack}
                  className="shrink-0 px-2.5 py-2 text-sm bg-neutral-light border border-neutral-border rounded-lg text-neutral-text hover:bg-primary/10 transition-colors whitespace-nowrap"
                >
                  Назад
                </button>
                <button
                  onClick={handleDocumentation}
                  className="shrink-0 px-2.5 py-2 text-sm bg-white border border-neutral-border rounded-lg shadow-sm text-neutral-text hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
                  title="Документация"
                >
                  <BookOpen size={16} className="shrink-0" />
                  <span>Data Docs</span>
                </button>
                <button
                  onClick={handleBugReport}
                  className="shrink-0 px-2.5 py-2 text-sm bg-white border border-neutral-border rounded-lg shadow-sm text-neutral-text hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
                  title="Сообщить о баге"
                >
                  <Bug size={16} className="shrink-0" />
                  <span className="hidden sm:inline">Bug report</span>
                </button>
                {/* Переключатели AI Tutor / Roadmap — только на мобильном, на desktop остаётся плавающая кнопка справа */}
                {isMobile && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setTutorAsideMode("chat");
                        setShowAITutor(true);
                      }}
                      className={`shrink-0 px-2.5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                        showAITutor && tutorAsideMode === "chat"
                          ? "bg-primary/15 text-primary border-2 border-primary/40"
                          : "bg-white border-2 border-neutral-border shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30"
                      }`}
                      title="Чат AI Tutor"
                      aria-label="Чат AI Tutor"
                    >
                      <MessageCircle size={18} className="shrink-0" />
                      <span className={showAITutor && tutorAsideMode === "chat" ? "text-primary font-medium" : ""}>
                        Чат
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTutorAsideMode("roadmap");
                        setShowAITutor(true);
                      }}
                      className={`shrink-0 px-2.5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                        showAITutor && tutorAsideMode === "roadmap"
                          ? "bg-primary/15 text-primary border-2 border-primary/40"
                          : "bg-white border-2 border-neutral-border shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30"
                      }`}
                      title="Roadmap"
                      aria-label="Roadmap"
                    >
                      <Map size={18} className="shrink-0" />
                      <span className={showAITutor && tutorAsideMode === "roadmap" ? "text-primary font-medium" : ""}>
                        Roadmap
                      </span>
                    </button>
                  </>
                )}
              </div>
              <div data-onboarding-id="sql-editor">
                <EditorSection
                  sql={sql}
                  setSql={setSql}
                  editorHeight={editorResize.size}
                  editorRef={editorResize.elementRef}
                  onMouseDown={editorResize.onMouseDown}
                  onTouchStart={editorResize.onTouchStart}
                  datasets={[]}
                  domain={undefined}
                  showLineage={false}
                  showDocumentation={true}
                  onDocumentationClick={handleDocumentation}
                  onRun={() => runQuery(false)}
                  onDebug={handleDebug}
                  onFormat={formatSql}
                  onReset={resetResult}
                  onCopy={shareQuery}
                  showCheck={false}
                  executionTimeMs={executionTimeMs}
                  memoryUsageBytes={memoryUsageBytes}
                  readBytes={readBytes}
                  readRows={readRows}
                  writtenBytes={writtenBytes}
                  writtenRows={writtenRows}
                  resultBytes={resultBytes}
                  resultRows={resultRows}
                  cpuTimeUs={cpuTimeUs}
                />
              </div>

              {/* Баннер с ошибкой между редактором и результатами */}
              {errorMsg && errorBannerVisible && (
                <ErrorBanner
                  message={errorMsg}
                  fullMessage={errorMsgFull || undefined}
                  onClose={() => setErrorBannerVisible(false)}
                />
              )}

              {/* Toast только для системных сообщений (не ошибки) */}
              <Toast
                message={toast ? toast : null}
                type="info"
                autoHide={!!toast}
                duration={2500}
              />

              <div
                data-onboarding-id="results"
                className={`${isMobile ? "flex-shrink-0 w-full" : "flex-1"} mt-4 ${
                  isMobile ? "pb-4" : "pb-8"
                } min-h-0 flex flex-col ${isLoading ? "min-h-[min(200px,30vh)]" : ""}`}
                style={{ width: "100%", maxWidth: "100%", overflowX: "auto", overflowY: "visible" }}
              >
                {isLoading ? (
                  <Loading stage={QUERY_LOADING_STAGE_LABEL[queryLoadingPhase]} />
                ) : (
                  <ResultViewer
                    rows={result}
                    columns={resultColumns}
                    error_msg={errorMsg}
                    resultGroups={resultGroups}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Ресайзер AI Tutor (desktop) */}
          {!isMobile && showAITutor && (
            <div
              onMouseDown={aiTutorResize.onMouseDown}
              className="hidden lg:flex relative z-10 justify-center cursor-col-resize flex-shrink-0 h-full"
              style={{ width: "4px", margin: "0 1px" }}
            >
              <div
                className="w-1 rounded bg-transparent hover:bg-primary/40 transition-colors"
                style={{ height: "100%" }}
              />
            </div>
          )}

          {/* Правая колонка: AI Tutor (desktop) */}
          {!isMobile && (
            <aside
              ref={aiTutorResize.elementRef}
              className={`hidden lg:flex flex-shrink-0 flex-col border-l border-neutral-border bg-white overflow-hidden ${
                showAITutor ? "" : "!w-0"
              }`}
              style={
                showAITutor
                  ? {
                      width: Math.max(aiTutorResize.size, chatPanelMinWidth),
                      minWidth: chatPanelMinWidth,
                      transition: "none",
                    }
                  : undefined
              }
            >
              {showAITutor && (
                <AITutorEditorPanel
                  user={user}
                  taskId="sandbox"
                  taskTitle="Sandbox"
                  userCode={sql}
                  onClose={() => setShowAITutor(false)}
                  embedded
                  onMinWidthChange={setChatPanelMinWidth}
                  queryMetrics={queryMetrics}
                  onboardingPageKey="sandbox"
                  onboardingCompletedPages={onboardingCompletedPages}
                  onboardingStatusLoading={onboardingStatusLoading}
                  onStartOnboarding={() => onboardingStartRef.current?.()}
                  asideMode={tutorAsideMode}
                  onAsideModeChange={setTutorAsideMode}
                />
              )}
            </aside>
          )}
        </div>

        {/* Mobile: AI Tutor fullscreen overlay */}
        {isMobile && showAITutor && (
          <div className="lg:hidden fixed inset-0 z-50 flex flex-col bg-white">
            <AITutorEditorPanel
              user={user}
              taskId="sandbox"
              taskTitle="Sandbox"
              userCode={sql}
              onClose={() => setShowAITutor(false)}
              embedded
              queryMetrics={queryMetrics}
              onboardingPageKey="sandbox"
              onboardingCompletedPages={onboardingCompletedPages}
              onboardingStatusLoading={onboardingStatusLoading}
              onStartOnboarding={() => onboardingStartRef.current?.()}
              asideMode={tutorAsideMode}
              onAsideModeChange={setTutorAsideMode}
            />
          </div>
        )}

        {/* Модальное окно с трейс-логами */}
        <TraceLogsModal
          isOpen={isTraceModalOpen}
          onClose={() => setIsTraceModalOpen(false)}
          traceLogs={traceLogs}
          isLoading={isLoading}
          onRerun={runQueryDebug}
        />
      </div>
    </>
  );
};

